import React, {createContext, ReactNode, useContext, useEffect, useState} from 'react';
import authService, {AuthUser, SignInData, SignUpData} from '../services/AuthService';
import UserProfileService from '../services/UserProfileService';

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  signUp: (data: SignUpData) => Promise<AuthUser>;
  signIn: (data: SignInData) => Promise<AuthUser>;
  signInWithGoogle: () => Promise<AuthUser>;
  signInAnonymously: () => Promise<AuthUser>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged(async (user) => {
      setUser(user);
      
      if (user) {
        try {
          await UserProfileService.syncUserProfile(user);
          console.log('✅ User profile synced to Firestore');
        } catch (error) {
          console.error('❌ Failed to sync user profile:', error);
        }
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signUp = async (data: SignUpData): Promise<AuthUser> => {
    setLoading(true);
    try {
      return await authService.signUpWithEmail(data);
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const signIn = async (data: SignInData): Promise<AuthUser> => {
    setLoading(true);
    try {
      return await authService.signInWithEmail(data);
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const signInWithGoogle = async (): Promise<AuthUser> => {
    setLoading(true);
    try {
      return await authService.signInWithGoogle();
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const signInAnonymously = async (): Promise<AuthUser> => {
    setLoading(true);
    try {
      const result = await authService.signInAnonymously();
      // Refresh user data to ensure the generated name is immediately visible
      await refreshUser();
      return result;
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const signOut = async (): Promise<void> => {
    setLoading(true);
    try {
      await authService.signOut();
      setUser(null);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async (): Promise<void> => {
    try {
      const currentUser = authService.getCurrentUser();
      if (currentUser) {
        // Reload the user to get the latest profile data
        await currentUser.reload();
        setUser({
          uid: currentUser.uid,
          email: currentUser.email,
          displayName: currentUser.displayName,
          photoURL: currentUser.photoURL,
          isAnonymous: currentUser.isAnonymous,
        });
      }
    } catch (error) {
      console.error('❌ Failed to refresh user data:', error);
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signInAnonymously,
    signOut,
    refreshUser,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 